import React, { useEffect, useRef, useState } from "react";

import Image from "next/image";

import classNames from "classnames";

import Container from "components/Container";

import ClockIcon from "assets/home/more-focus/clock-icon.png";
import TerminalIcon from "assets/home/more-focus/terminal-icon.png";
import CommandsIcon from "assets/home/more-focus/commands-icon.png";
import LockIcon from "assets/home/more-focus/lock-icon.png";

import styles from "./MoreFocus.module.css";

import Subtitle from "./Subtitle";

const reasons = [
  <>
    <Image src={LockIcon} width={48} height={48} layout="fixed" alt="A Lock Icon" />
    <strong>Security by default, always</strong>
    <p>
      Enable the built-in extensions for an initial productivity boost. Use script commands and our API to build custom
      extensions to optimize your workflow.
    </p>
  </>,

  <>
    <Image src={CommandsIcon} width={48} height={48} layout="fixed" alt="An icon resembling a computer chip" />
    <strong>Make it your own, tailor it to you</strong>
    <p>
      Everything is stored encrypted on your local disk, and nothing sensetive is tracked. You and your personal data
      stay anonymous.
    </p>
  </>,

  <>
    <Image src={TerminalIcon} width={48} height={48} layout="fixed" alt="An icon resembling a terminal input" />
    <strong>Learn once, remember forever</strong>
    <p>
      Inspired by the command line interface, your tools are unified as commands, and follow the same structure to make
      you more productive.
    </p>
  </>,

  <>
    <Image src={ClockIcon} width={48} height={48} layout="fixed" alt="An icon resembling a clock" />
    <strong>Perform tasks in a matter of seconds</strong>
    <p>
      Raycast is built natively to be faster, lightweight, and efficient. Every action is optimized for speed and
      accessibility.
    </p>
  </>,
];

const MoreFocus: React.FC = () => {
  const [mouseEntered, setMouseEntered] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const intervalTimer = useRef<NodeJS.Timer>();

  useEffect(() => {
    if (!mouseEntered) {
      intervalTimer.current = setInterval(() => {
        setFocusedIndex((index) => (index + 1) % reasons.length);
      }, 4000);
    }

    return () => {
      window.clearInterval(intervalTimer.current);
    };
  }, [mouseEntered]);

  return (
    <>
      <Subtitle>
        <h3>
          More focus, <span className={styles.lessClutter}>less clutter.</span>
        </h3>
        <p>Keep your workspace centralized, clean and tidy. Engineered with performance and your privacy in mind.</p>
      </Subtitle>
      <Container className={styles.reasons}>
        {reasons.map((reason, index) => (
          <div
            className={classNames(styles.reason, { [styles.focused]: focusedIndex == index })}
            key={index}
            onMouseEnter={() => {
              setFocusedIndex(index);
              setMouseEntered(true);
            }}
            onMouseLeave={() => setMouseEntered(false)}
          >
            {reason}
          </div>
        ))}
      </Container>
    </>
  );
};

export default MoreFocus;
